import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { RealtimeProvider } from "./contexts/RealtimeContext";
import { CurrencyProvider } from "./contexts/CurrencyContext";
import { Toaster } from "./components/ui/sonner";
import ProtectedRoute from "./components/ProtectedRoute";
import ScrollToTopOnRouteChange from "./components/ScrollToTopOnRouteChange";

// Pages
import LandingPage from "./pages/LandingPage";
import TermsPage from "./pages/TermsPage";
import AboutPage from "./pages/AboutPage";
import PrivacyPage from "./pages/PrivacyPage";
import GdprPage from "./pages/GdprPage";
import LoginPage from "./pages/auth/LoginPage";
import RegisterPage from "./pages/auth/RegisterPage";
import VerifyEmailRequiredPage from "./pages/auth/VerifyEmailRequiredPage";
import VerifyEmailPage from "./pages/auth/VerifyEmailPage";
import AcceptInvitationPage from "./pages/auth/AcceptInvitationPage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";

// Dashboard
import DashboardLayout from "./pages/dashboard/DashboardLayout";
import DashboardHome from "./pages/dashboard/DashboardHome";
import JobsListPage from "./pages/dashboard/JobsListPage";
import JobFormPage from "./pages/dashboard/JobFormPage";
import JobDetailPage from "./pages/dashboard/JobDetailPage";
import CandidatesListPage from "./pages/dashboard/CandidatesListPage";
import RecentCandidatesPage from "./pages/dashboard/RecentCandidatesPage";
import CandidateDetailPage from "./pages/dashboard/CandidateDetailPage";
import PipelinePage from "./pages/dashboard/PipelinePage";
import SubscriptionPage from "./pages/dashboard/SubscriptionPage";
import SettingsPage from "./pages/dashboard/SettingsPage";
import AdminPage from "./pages/dashboard/AdminPage";
import AdminUserDetailPage from "./pages/dashboard/AdminUserDetailPage";
import AdminTestimonialsPage from "./pages/dashboard/AdminTestimonialsPage";
import MyTestimonialPage from "./pages/dashboard/MyTestimonialPage";
import AuditLogsPage from "./pages/dashboard/AuditLogsPage";
import AdminGlobalAnalyticsPage from "./pages/dashboard/AdminGlobalAnalyticsPage";
import AdminScoringCachePage from "./pages/dashboard/AdminScoringCachePage";
import AdminScoringComparePage from "./pages/dashboard/AdminScoringComparePage";
import AdminCvExtractionCachePage from "./pages/dashboard/AdminCvExtractionCachePage";
import AdminLlmJournalPage from "./pages/dashboard/AdminLlmJournalPage";
import TrashPage from "./pages/dashboard/TrashPage";

function AppShell({ children }) {
    const { user } = useAuth();
    return <CurrencyProvider user={user}>{children}</CurrencyProvider>;
}

function App() {
    return (
        <AuthProvider>
            <RealtimeProvider>
            <AppShell>
                <div className="App">
                    <BrowserRouter>
                    <ScrollToTopOnRouteChange />
                    <Routes>
                        {/* Public routes */}
                        <Route path="/" element={<LandingPage />} />
                        <Route path="/about" element={<AboutPage />} />
                        <Route path="/privacy" element={<PrivacyPage />} />
                        <Route path="/gdpr" element={<GdprPage />} />
                        <Route path="/terms" element={<TermsPage />} />
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/register" element={<RegisterPage />} />
                        <Route path="/verify-email-required" element={<VerifyEmailRequiredPage />} />
                        <Route path="/verify-email" element={<VerifyEmailPage />} />
                        <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
                        <Route path="/reset-password" element={<ResetPasswordPage />} />
                        <Route path="/forgot-password" element={<ForgotPasswordPage />} />

                        {/* Protected dashboard routes */}
                        <Route
                            path="/dashboard"
                            element={
                                <ProtectedRoute allowBilling>
                                    <DashboardLayout />
                                </ProtectedRoute>
                            }
                        >
                            <Route index element={<ProtectedRoute><DashboardHome /></ProtectedRoute>} />
                            <Route path="jobs" element={<ProtectedRoute><JobsListPage /></ProtectedRoute>} />
                            <Route path="jobs/new" element={<ProtectedRoute><JobFormPage /></ProtectedRoute>} />
                            <Route path="jobs/:id" element={<ProtectedRoute><JobDetailPage /></ProtectedRoute>} />
                            <Route path="jobs/:id/edit" element={<ProtectedRoute><JobFormPage /></ProtectedRoute>} />
                            <Route path="candidates" element={<ProtectedRoute><CandidatesListPage /></ProtectedRoute>} />
                            <Route path="candidates/recent" element={<ProtectedRoute><RecentCandidatesPage /></ProtectedRoute>} />
                            <Route path="candidates/:id" element={<ProtectedRoute><CandidateDetailPage /></ProtectedRoute>} />
                            <Route path="pipeline" element={<ProtectedRoute><PipelinePage /></ProtectedRoute>} />
                            <Route path="trash" element={<ProtectedRoute><TrashPage /></ProtectedRoute>} />
                            <Route path="settings" element={<ProtectedRoute allowBilling><SubscriptionPage /></ProtectedRoute>} />
                            <Route path="settings/profile" element={<ProtectedRoute allowBilling><SettingsPage /></ProtectedRoute>} />
                            <Route path="settings/subscription" element={<ProtectedRoute allowBilling><SubscriptionPage /></ProtectedRoute>} />
                            <Route
                                path="admin"
                                element={
                                    <ProtectedRoute requireAdmin>
                                        <AdminPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="admin/users/:userId"
                                element={
                                    <ProtectedRoute requireAdmin>
                                        <AdminUserDetailPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="admin/audit"
                                element={
                                    <ProtectedRoute allowedRoles={['admin', 'manager', 'billing_admin']}>
                                        <AuditLogsPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="admin/analytics"
                                element={
                                    <ProtectedRoute requireAdmin>
                                        <AdminGlobalAnalyticsPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="admin/scoring-cache"
                                element={
                                    <ProtectedRoute requireAdmin>
                                        <AdminScoringCachePage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="admin/scoring-compare"
                                element={
                                    <ProtectedRoute requireAdmin>
                                        <AdminScoringComparePage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="admin/cv-extraction-cache"
                                element={
                                    <ProtectedRoute requireAdmin>
                                        <AdminCvExtractionCachePage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="admin/llm-journal"
                                element={
                                    <ProtectedRoute requireAdmin>
                                        <AdminLlmJournalPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="testimonials"
                                element={
                                    <ProtectedRoute allowedRoles={['admin', 'manager', 'recruiter', 'billing_admin']}>
                                        <MyTestimonialPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="admin/testimonials"
                                element={
                                    <ProtectedRoute requireAdmin>
                                        <AdminTestimonialsPage />
                                    </ProtectedRoute>
                                }
                            />
                        </Route>

                        {/* Catch all */}
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </BrowserRouter>
                <Toaster position="top-right" richColors />
            </div>
            </AppShell>
            </RealtimeProvider>
        </AuthProvider>
    );
}

export default App;
